import type { FontDefinition } from './types';

const FONT_ASSET_ROOT = '/configurator-fonts';
const fontAsset = (filename: string) => `${FONT_ASSET_ROOT}/${filename}`;

const interLatin = fontAsset('inter-latin-wght-normal.woff2');
const interLatinExt = fontAsset('inter-latin-ext-wght-normal.woff2');
const interItalicLatin = fontAsset('inter-latin-wght-italic.woff2');
const interItalicLatinExt = fontAsset('inter-latin-ext-wght-italic.woff2');
const robotoLatin = fontAsset('roboto-latin-wght-normal.woff2');
const robotoLatinExt = fontAsset('roboto-latin-ext-wght-normal.woff2');
const montserratLatin = fontAsset('montserrat-latin-wght-normal.woff2');
const montserratLatinExt = fontAsset('montserrat-latin-ext-wght-normal.woff2');
const openSansLatin = fontAsset('open-sans-latin-wght-normal.woff2');
const openSansLatinExt = fontAsset('open-sans-latin-ext-wght-normal.woff2');
const oswaldLatin = fontAsset('oswald-latin-wght-normal.woff2');
const oswaldLatinExt = fontAsset('oswald-latin-ext-wght-normal.woff2');
const playfairLatin = fontAsset('playfair-display-latin-wght-normal.woff2');
const playfairLatinExt = fontAsset('playfair-display-latin-ext-wght-normal.woff2');
const playfairItalicLatin = fontAsset('playfair-display-latin-wght-italic.woff2');
const playfairItalicLatinExt = fontAsset('playfair-display-latin-ext-wght-italic.woff2');
const loraLatin = fontAsset('lora-latin-wght-normal.woff2');
const loraLatinExt = fontAsset('lora-latin-ext-wght-normal.woff2');
const robotoSlabLatin = fontAsset('roboto-slab-latin-wght-normal.woff2');
const robotoSlabLatinExt = fontAsset('roboto-slab-latin-ext-wght-normal.woff2');
const bebasNeueLatin = fontAsset('bebas-neue-latin-400-normal.woff2');
const bebasNeueLatinExt = fontAsset('bebas-neue-latin-ext-400-normal.woff2');
const antonLatin = fontAsset('anton-latin-400-normal.woff2');
const antonLatinExt = fontAsset('anton-latin-ext-400-normal.woff2');
const pacificoLatin = fontAsset('pacifico-latin-400-normal.woff2');
const pacificoLatinExt = fontAsset('pacifico-latin-ext-400-normal.woff2');
const lobsterLatin = fontAsset('lobster-latin-400-normal.woff2');
const lobsterLatinExt = fontAsset('lobster-latin-ext-400-normal.woff2');

const OFL = 'SIL Open Font License 1.1';
const LATIN_RANGE =
  'U+0000-00FF,U+0131,U+0152-0153,U+02BB-02BC,U+02C6,U+02DA,U+02DC,U+0304,U+0308,U+0329,U+2000-206F,U+20AC,U+2122,U+2191,U+2193,U+2212,U+2215,U+FEFF,U+FFFD';
const LATIN_EXT_RANGE =
  'U+0100-02BA,U+02BD-02C5,U+02C7-02CC,U+02CE-02D7,U+02DD-02FF,U+0304,U+0308,U+0329,U+1D00-1DBF,U+1E00-1E9F,U+1EF2-1EFF,U+2020,U+20A0-20AB,U+20AD-20C0,U+2113,U+2C60-2C7F,U+A720-A7FF';
export const FONT_PREVIEW_SAMPLE = 'Váš text · Čierne tričko · Žilina 123';

export const fonts: FontDefinition[] = [
  {
    id: 'font-inter',
    family: 'Inter',
    label: 'Inter',
    category: 'sans-serif',
    weights: [400, 600, 700, 800],
    styles: ['normal', 'italic'],
    sourceUrl: interLatin,
    extendedSourceUrl: interLatinExt,
    italicSourceUrl: interItalicLatin,
    italicExtendedSourceUrl: interItalicLatinExt,
    variableWeightRange: { min: 100, max: 900 },
    license: OFL,
    fallback: 'Arial, sans-serif',
  },
  {
    id: 'font-roboto',
    family: 'Roboto',
    label: 'Roboto',
    category: 'sans-serif',
    weights: [400, 600, 700, 800],
    styles: ['normal'],
    sourceUrl: robotoLatin,
    extendedSourceUrl: robotoLatinExt,
    variableWeightRange: { min: 100, max: 900 },
    license: OFL,
    fallback: 'Arial, sans-serif',
  },
  {
    id: 'font-montserrat',
    family: 'Montserrat',
    label: 'Montserrat',
    category: 'sans-serif',
    weights: [400, 600, 700, 800],
    styles: ['normal'],
    sourceUrl: montserratLatin,
    extendedSourceUrl: montserratLatinExt,
    variableWeightRange: { min: 100, max: 900 },
    license: OFL,
    fallback: 'Arial, sans-serif',
  },
  {
    id: 'font-open-sans',
    family: 'Open Sans',
    label: 'Open Sans',
    category: 'sans-serif',
    weights: [400, 600, 700, 800],
    styles: ['normal'],
    sourceUrl: openSansLatin,
    extendedSourceUrl: openSansLatinExt,
    variableWeightRange: { min: 300, max: 800 },
    license: OFL,
    fallback: 'Arial, sans-serif',
  },
  {
    id: 'font-oswald',
    family: 'Oswald',
    label: 'Oswald',
    category: 'display',
    weights: [400, 600, 700],
    styles: ['normal'],
    sourceUrl: oswaldLatin,
    extendedSourceUrl: oswaldLatinExt,
    variableWeightRange: { min: 200, max: 700 },
    license: OFL,
    fallback: 'Arial, sans-serif',
  },
  {
    id: 'font-playfair',
    family: 'Playfair Display',
    label: 'Playfair Display',
    category: 'serif',
    weights: [400, 600, 700, 800],
    styles: ['normal', 'italic'],
    sourceUrl: playfairLatin,
    extendedSourceUrl: playfairLatinExt,
    italicSourceUrl: playfairItalicLatin,
    italicExtendedSourceUrl: playfairItalicLatinExt,
    variableWeightRange: { min: 400, max: 900 },
    license: OFL,
    fallback: 'Georgia, serif',
  },
  {
    id: 'font-lora',
    family: 'Lora',
    label: 'Lora',
    category: 'serif',
    weights: [400, 600, 700],
    styles: ['normal'],
    sourceUrl: loraLatin,
    extendedSourceUrl: loraLatinExt,
    variableWeightRange: { min: 400, max: 700 },
    license: OFL,
    fallback: 'Georgia, serif',
  },
  {
    id: 'font-roboto-slab',
    family: 'Roboto Slab',
    label: 'Roboto Slab',
    category: 'serif',
    weights: [400, 600, 700, 800],
    styles: ['normal'],
    sourceUrl: robotoSlabLatin,
    extendedSourceUrl: robotoSlabLatinExt,
    variableWeightRange: { min: 100, max: 900 },
    license: 'Apache License 2.0',
    fallback: 'Georgia, serif',
  },
  {
    id: 'font-bebas-neue',
    family: 'Bebas Neue',
    label: 'Bebas Neue',
    category: 'display',
    weights: [400],
    styles: ['normal'],
    sourceUrl: bebasNeueLatin,
    extendedSourceUrl: bebasNeueLatinExt,
    license: OFL,
    fallback: 'Impact, sans-serif',
  },
  {
    id: 'font-anton',
    family: 'Anton',
    label: 'Anton',
    category: 'display',
    weights: [400],
    styles: ['normal'],
    sourceUrl: antonLatin,
    extendedSourceUrl: antonLatinExt,
    license: OFL,
    fallback: 'Impact, sans-serif',
  },
  {
    id: 'font-pacifico',
    family: 'Pacifico',
    label: 'Pacifico',
    category: 'handwriting',
    weights: [400],
    styles: ['normal'],
    sourceUrl: pacificoLatin,
    extendedSourceUrl: pacificoLatinExt,
    license: OFL,
    fallback: 'cursive',
  },
  {
    id: 'font-lobster',
    family: 'Lobster',
    label: 'Lobster',
    category: 'handwriting',
    weights: [400],
    styles: ['normal'],
    sourceUrl: lobsterLatin,
    extendedSourceUrl: lobsterLatinExt,
    license: OFL,
    fallback: 'cursive',
  },
];

const fontLoads = new Map<string, Promise<void>>();

export function closestFontWeight(font: FontDefinition, requested: number): number {
  return font.weights.reduce(
    (closest, weight) =>
      Math.abs(weight - requested) < Math.abs(closest - requested) ? weight : closest,
    font.weights[0] ?? 400,
  );
}

export async function ensureFontLoaded(
  font: FontDefinition,
  weight = 400,
  style: 'normal' | 'italic' = 'normal',
): Promise<void> {
  if (typeof document === 'undefined' || typeof FontFace === 'undefined') return;
  if (!font.styles.includes(style))
    throw new Error(`Font ${font.family} does not support ${style}`);
  const range = font.variableWeightRange;
  if (range ? weight < range.min || weight > range.max : !font.weights.includes(weight)) {
    throw new Error(`Font ${font.family} does not support weight ${weight}`);
  }

  const baseUrl = style === 'italic' ? font.italicSourceUrl : font.sourceUrl;
  const extendedUrl = style === 'italic' ? font.italicExtendedSourceUrl : font.extendedSourceUrl;
  if (!baseUrl) throw new Error(`Font ${font.family} has no ${style} source`);
  const weightDescriptor = range ? `${range.min} ${range.max}` : String(weight);
  const key = [font.id, style, weightDescriptor, baseUrl, extendedUrl].join('|');
  let load = fontLoads.get(key);
  if (!load) {
    load = (async () => {
      const sources = [
        { url: baseUrl, unicodeRange: LATIN_RANGE },
        ...(extendedUrl ? [{ url: extendedUrl, unicodeRange: LATIN_EXT_RANGE }] : []),
      ];
      const faces = await Promise.all(
        sources.map(async ({ url, unicodeRange }) => {
          const face = new FontFace(font.family, `url(${JSON.stringify(url)})`, {
            style,
            weight: weightDescriptor,
            unicodeRange,
          });
          await face.load();
          return face;
        }),
      );
      faces.forEach(face => document.fonts.add(face));
      const descriptor = `${style} ${weight} 16px "${font.family}"`;
      if (!document.fonts.check(descriptor, FONT_PREVIEW_SAMPLE)) {
        throw new Error(`Font ${font.family} failed deterministic loading`);
      }
    })();
    fontLoads.set(key, load);
    load.catch(() => fontLoads.delete(key));
  }
  await load;
}
